import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

const LanguageSwitch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  const handleLanguageSelect = (language: string): void => {
    void i18n.changeLanguage(language);
    setIsOpen(false);
  };

  const languages = [
    { code: "pl", label: "PL" },
    { code: "en", label: "EN" },
    { code: "ua", label: "UA" },
    { code: "ru", label: "RU" },
  ];

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative flex items-center" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="inline-flex h-8 items-center rounded-full border border-primary px-2.5 text-body"
      >
        <Globe size={16} className="mr-1.5" aria-hidden="true" />
        <span className="text-sm font-bold">{t("language")}</span>
      </button>

      {isOpen && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 overflow-hidden rounded-lg bg-surface shadow-lg ring-1 ring-black/5"
        >
          {languages.map(({ code, label }) => (
            <li key={code} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  handleLanguageSelect(code);
                }}
                className={`flex w-full px-5 py-2 text-sm font-medium transition-colors hover:bg-primary/10 hover:text-primary ${
                  i18n.language === code
                    ? "bg-primary/10 text-primary"
                    : "bg-surface text-body"
                }`}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitch;
